import { useState } from "react";
import {
  Box, Paper, Stack, TextField, Button, Alert, FormControlLabel, Switch, MenuItem, Typography,
  Table, TableBody, TableCell, TableHead, TableRow, Grid2 as Grid,
} from "@mui/material";
import { useQuery, useMutation } from "@tanstack/react-query";
import { diagnosticsApi } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { PageHeader } from "@/components/PageHeader";
import { DiagnosticOrderIdField } from "@/components/DiagnosticOrderIdField";
import { formatDateTime } from "@/shared/datetime";

/**
 * Radiology scheduling — slot capacity, slot availability, and booking/acquiring a study, in the order
 * the workflow runs: create slots → find a free one → schedule the study into it → mark it performed.
 */
export function RadiologySchedulingPage() {
  const err = (...ms: { isError: boolean; error: unknown }[]) =>
    ms.filter(m => m.isError).map((m, i) => <Alert key={i} severity="error" sx={{ mt: 1 }}>{errorMessage(m.error)}</Alert>);

  const canManageSlots = useAuth(s => s.hasPermission("diagnostics.catalog.manage"));
  const canSchedule = useAuth(s => s.hasPermission("diagnostics.study.schedule"));
  const canPerform = useAuth(s => s.hasPermission("diagnostics.study.perform"));

  const modalities = useQuery({ queryKey: ["dx-modalities"], queryFn: () => diagnosticsApi.listModalities() });
  const procedures = useQuery({ queryKey: ["dx-procedures"], queryFn: () => diagnosticsApi.listProcedures() });
  const modalityMenuItems = () => (modalities.data ?? []).map(m => <MenuItem key={m.id} value={m.id}>{m.code} — {m.name}</MenuItem>);

  // ---- slot capacity ----
  const [sMod, setSMod] = useState(""); const [sStart, setSStart] = useState(""); const [sEnd, setSEnd] = useState("");
  const slot = useMutation({
    meta: { successMessage: "Slot created" },
    mutationFn: () => diagnosticsApi.createSlot({ modalityId: sMod, startUtc: new Date(sStart).toISOString(), endUtc: new Date(sEnd).toISOString() }),
  });

  // ---- availability ----
  const [listMod, setListMod] = useState("");
  const slots = useQuery({ queryKey: ["dx-slots", listMod], queryFn: () => diagnosticsApi.listSlots(listMod), enabled: !!listMod });

  // ---- schedule / perform ----
  const [stOrder, setStOrder] = useState(""); const [stProc, setStProc] = useState(""); const [stSlot, setStSlot] = useState("");
  const schedule = useMutation({
    meta: { successMessage: "Study scheduled (slot booked)" },
    mutationFn: () => diagnosticsApi.scheduleStudy({ diagnosticOrderId: stOrder, procedureId: stProc, modalitySlotId: stSlot }),
  });
  const [studyId, setStudyId] = useState(""); const [uid, setUid] = useState(""); const [tech, setTech] = useState(""); const [contrast, setContrast] = useState(false);
  const perform = useMutation({
    meta: { successMessage: "Study acquired" },
    mutationFn: () => diagnosticsApi.performStudy(studyId, { studyInstanceUid: uid, technologist: tech || null, contrastUsed: contrast }),
  });

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Diagnostics", "Radiology"]}
        title="Scheduling"
        subtitle="Slot capacity, availability, and scheduling or performing a study."
      />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Create slot</Typography>
            <Stack spacing={1.5}>
              <TextField select label="Modality" size="small" value={sMod} onChange={e => setSMod(e.target.value)} disabled={!canManageSlots}>
                <MenuItem value="">— select modality —</MenuItem>
                {modalityMenuItems()}
              </TextField>
              <TextField label="Start" type="datetime-local" size="small" InputLabelProps={{ shrink: true }}
                value={sStart} onChange={e => setSStart(e.target.value)} disabled={!canManageSlots} />
              <TextField label="End" type="datetime-local" size="small" InputLabelProps={{ shrink: true }}
                value={sEnd} onChange={e => setSEnd(e.target.value)} disabled={!canManageSlots} />
              <Button variant="contained" onClick={() => slot.mutate()}
                disabled={!canManageSlots || slot.isPending || !sMod || !sStart || !sEnd}>Create slot</Button>
            </Stack>{err(slot)}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Available slots</Typography>
            <TextField select label="Modality" size="small" fullWidth value={listMod} onChange={e => setListMod(e.target.value)} sx={{ mb: 1 }}>
              <MenuItem value="">— select modality —</MenuItem>
              {modalityMenuItems()}
            </TextField>
            {slots.isError && <Alert severity="error">{errorMessage(slots.error)}</Alert>}
            <Table size="small">
              <TableHead><TableRow><TableCell>Slot Id</TableCell><TableCell>Start</TableCell><TableCell>End</TableCell></TableRow></TableHead>
              <TableBody>
                {(slots.data ?? []).map(s => (
                  <TableRow key={s.id} hover>
                    <TableCell sx={{ fontSize: 11 }}>{s.id}</TableCell>
                    <TableCell>{formatDateTime(s.startUtc)}</TableCell>
                    <TableCell>{formatDateTime(s.endUtc)}</TableCell>
                  </TableRow>
                ))}
                {listMod && slots.data?.length === 0 && (
                  <TableRow><TableCell colSpan={3}>
                    <Typography variant="caption" color="text.secondary">No free slots for this modality.</Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Schedule study</Typography>
            <Stack spacing={1.5}>
              <DiagnosticOrderIdField value={stOrder} onChange={setStOrder} required />
              <TextField select label="Procedure" size="small" value={stProc} onChange={e => setStProc(e.target.value)}>
                <MenuItem value="">— select procedure —</MenuItem>
                {(procedures.data ?? []).map(p => <MenuItem key={p.id} value={p.id}>{p.code} — {p.name}</MenuItem>)}
              </TextField>
              <TextField label="Slot Id" size="small" value={stSlot} onChange={e => setStSlot(e.target.value)}
                helperText="Copy from Available slots above." />
              <Button variant="contained" onClick={() => schedule.mutate()}
                disabled={!canSchedule || schedule.isPending || !stOrder || !stProc || !stSlot}>Schedule (book slot)</Button>
            </Stack>{err(schedule)}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Perform study</Typography>
            <Stack spacing={1.5}>
              <TextField label="Study Id" size="small" value={studyId} onChange={e => setStudyId(e.target.value)} />
              <Stack direction="row" spacing={1}>
                <TextField label="DICOM UID" size="small" value={uid} onChange={e => setUid(e.target.value)} sx={{ flexGrow: 1 }} />
                <TextField label="Technologist" size="small" value={tech} onChange={e => setTech(e.target.value)} sx={{ width: 140 }} />
              </Stack>
              <FormControlLabel control={<Switch checked={contrast} onChange={(_, v) => setContrast(v)} />} label="Contrast used" />
              <Button variant="contained" onClick={() => perform.mutate()}
                disabled={!canPerform || perform.isPending || !studyId || !uid}>Perform</Button>
            </Stack>{err(perform)}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
